import { useState, createContext, useEffect } from 'react'
import { toast } from 'react-toastify'

import { useTableData } from '@/hooks/useTableData'
import { usePermission } from '@/hooks/usePermission'
import { usersService } from '@/services/Users'
import { authenticationService } from '@/services/Login'

import { CONSTANTS, SUCCESS_STATUS } from '@/utils/CONSTANTS'
import { PermissionAction, PermissionResource } from '@/types/permissions.types'

interface IUsers {
  children: React.ReactNode
}

type UsersContextData = {
  USERS_DATA: any
  USER: any
  GET_USERS: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_USERS_BY_ID: (params?: any) => void
  CREATE_USER: (params?: any) => void
  APROVE_USERS: (params?: any) => Promise<any>
  REPROVE_USERS: (params?: any) => Promise<any>
  UPDATE_USER: (params?: any) => void
  CAN_APROVAL_USERS?: boolean
}

export const UsersContext = createContext({} as UsersContextData)

export function UsersProvider({ children }: IUsers) {
  const [USERS_DATA, SET_USERS_DATA] = useState({})
  const [USER, SET_USER] = useState([])

  const { PERMISSIONS, HAS_PERMISSION } = usePermission()
  const [CAN_APROVAL_USERS, SET_CAN_APROVAL_USERS] = useState(false)

  useEffect(() => {
    const canUserAproval = HAS_PERMISSION(
      PermissionResource.USER,
      PermissionAction.APROVE
    )
    SET_CAN_APROVAL_USERS(canUserAproval)
  }, [PERMISSIONS, HAS_PERMISSION])

  async function GET_USERS(params?: any, isFilterApplied?: boolean) {
    try {
      const res = await usersService.get({
        ...params
      })
      const users = res.data.data

      if (!users?.rows?.length) {
        SET_USERS_DATA([])
        return toast.warning(CONSTANTS.filter.empty)
      }

      if (users?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_USERS_DATA(res.data.data)
      return users
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_USERS_BY_ID(params: any) {
    try {
      const res = await usersService.get(params)
      SET_USER(res.data.data.rows[0])
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function CREATE_USER(params: any) {
    try {
      ;(await authenticationService.createCustomer(params), GET_USERS())
      toast.success('Usuário criado com sucesso!')
    } catch (e: any) {
      toast.error(e.message)
      throw new Error()
    }
  }

  async function UPDATE_USER(params: any) {
    try {
      const response: any = await authenticationService.updateCustomer(params)
      GET_USERS()
      const { success, messages } = response?.data || {}
      if (success && Array.isArray(messages) && messages.length > 0) {
        toast.warning(messages.join('<br/>'))
      }
      toast.success('Usuário atualizado com sucesso!')
    } catch (e: any) {
      toast.error(e.message)
      throw new Error()
    }
  }

  async function REPROVE_USERS(params: any) {
    try {
      const responseReprove = await usersService.reprove(params)

      if (SUCCESS_STATUS.includes(responseReprove?.status)) {
        const messages = responseReprove?.data?.messages?.[0]
        toast.success(messages || 'Sua solicitação foi atendida com sucesso.')
      }
      return responseReprove
    } catch (e: any) {
      toast.error(e.message)
      throw new Error()
    }
  }
  async function APROVE_USERS(params: any) {
    try {
      const responseApprove = await authenticationService.approve(params)

      if (SUCCESS_STATUS.includes(responseApprove?.status)) {
        const messages = responseApprove?.data?.messages?.[0]
        toast.success(messages || 'Sua solicitação foi atendida com sucesso.')
      }
      return responseApprove
    } catch (e: any) {
      toast.error(e.message)
      throw new Error()
    }
  }

  return (
    <UsersContext.Provider
      value={{
        GET_USERS,
        GET_USERS_BY_ID,
        USER,
        USERS_DATA,
        CREATE_USER,
        REPROVE_USERS,
        APROVE_USERS,
        UPDATE_USER,
        CAN_APROVAL_USERS
      }}
    >
      {children}
    </UsersContext.Provider>
  )
}
